import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { Search, Check, ChevronDown } from 'lucide-react';

// Lists longer than this get a search box; shorter ones open straight to the options.
const SEARCH_THRESHOLD = 10;
const PAGE_STEP = 8;
const TYPEAHEAD_RESET_MS = 700;

/**
 * SearchSelect - the STUI dropdown. Every single-select in the app is drawn by this component
 * (directly, or through StuiSelect for <select>-style markup); the browser's own select popup
 * is never shown, so the open list follows the theme in every browser.
 *
 * Keyboard, matching a native select:
 * - Closed: Up / Down / Home / End change the value in place; typing jumps to the first option
 *   starting with the typed text; Enter, Space, F4 or Alt+Down open the list.
 * - Open: Up / Down / Home / End / PageUp / PageDown move the highlight, Enter picks it, Escape
 *   or Alt+Up closes, Tab closes and moves on. Typing filters (search box) or jumps (no box).
 *
 * Props:
 * - value: Current selected value (id for FK, string for lookup)
 * - displayValue: Display text for current selection
 * - options: Array of { id, display, disabled? } for FK or strings for lookup
 * - onChange: (value) => void
 * - placeholder: Placeholder text when nothing selected
 * - disabled: Disable the control
 * - isLookup: true if options are strings (lookup category), false for FK objects
 * - searchable: 'auto' (search box above SEARCH_THRESHOLD options), true or false
 * - clearable: show a "(Clear selection)" row while a value is set
 * - sizer: text the trigger reserves width for, so an auto-width trigger keeps one width as the
 *   value changes (a native select sizes to its longest option the same way)
 * - size: 'xs' for the compact variant (inline-add rows in a header bar). Applied to the
 *   trigger and to the portalled dropdown, which a className on the trigger cannot reach.
 * - compactMenu: the dropdown alone takes the compact text size, for a compact trigger sized by
 *   its own class (StuiSelect's .stui-select, .stui-ds-cell-input)
 * - id, title, style, aria-label, onClick: passed to the trigger
 */
const SearchSelect = forwardRef(({
  value,
  displayValue,
  options = [],
  onChange,
  placeholder = '-- Select --',
  disabled = false,
  isLookup = false,
  searchable = 'auto',
  clearable = true,
  sizer,
  className = '',
  size,
  compactMenu = false,
  id,
  title,
  style,
  'aria-label': ariaLabel,
  onClick,
}, ref) => {
  const sizeClass = size === 'xs' ? 'stui-searchselect-xs' : '';
  const menuSizeClass = size === 'xs' || compactMenu ? 'stui-searchselect-xs' : '';
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [openAbove, setOpenAbove] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionsRef = useRef(null);
  const typeahead = useRef({ text: '', at: 0 });
  const dropdownId = useRef(`searchselect-${Math.random().toString(36).substr(2, 9)}`).current;

  useImperativeHandle(ref, () => containerRef.current);

  // Normalize options to { id, display, disabled } format
  const normalizedOptions = isLookup
    ? options.map(opt => ({ id: opt, display: opt }))
    : options;

  const showSearch = searchable === 'auto' ? normalizedOptions.length > SEARCH_THRESHOLD : !!searchable;

  // Filter options by search
  const filteredOptions = normalizedOptions.filter(opt =>
    String(opt.display ?? '').toLowerCase().includes(search.toLowerCase())
  );

  const hasValue = value !== null && value !== undefined && value !== '';
  const showClear = clearable && hasValue;
  const isSelected = (opt) => (isLookup ? value === opt.display : value === opt.id);

  // The rows the highlight moves over: the clear row (when shown), then the filtered options.
  const rows = showClear ? [{ clear: true }, ...filteredOptions] : filteredOptions;
  const enabled = (i) => rows[i] && !rows[i].disabled;
  const optionDomId = (i) => `${dropdownId}-opt-${i}`;

  // Calculate dropdown position
  const calculatePosition = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const dropdown = dropdownRef.current;
    const dropdownHeight = dropdown?.offsetHeight || 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const above = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    setOpenAbove(above);

    if (dropdown) {
      dropdown.style.top = `${above ? rect.top - dropdownHeight - 2 : rect.bottom + 2}px`;
      // With a search box the list keeps room for typing; without one it sizes to its options,
      // never narrower than the trigger.
      dropdown.style.width = showSearch ? `${Math.max(rect.width, 200)}px` : '';
      dropdown.style.minWidth = showSearch ? '' : `${rect.width}px`;
      dropdown.style.maxWidth = showSearch ? '' : `${Math.max(400, rect.width)}px`;
      // A CSS min-width can make the dropdown wider than its trigger; keep it on screen.
      const width = dropdown.offsetWidth;
      dropdown.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))}px`;
    }
  }, [showSearch]);

  useLayoutEffect(() => {
    if (isOpen) calculatePosition();
  }, [isOpen, calculatePosition, search]);

  const focusTrigger = () => containerRef.current?.focus({ preventScroll: true });

  // Open dropdown, highlighting the current value
  const handleOpen = () => {
    if (disabled) return;
    const selected = normalizedOptions.findIndex(isSelected);
    const offset = clearable && hasValue ? 1 : 0;
    setSearch('');
    setActiveIndex(selected >= 0 ? selected + offset : normalizedOptions.findIndex(o => !o.disabled) + offset);
    setIsOpen(true);
  };

  // Close dropdown
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearch('');
  }, []);

  const pick = (opt) => onChange(isLookup ? opt.display : opt.id);

  // Select option
  const handleSelect = (opt) => {
    if (opt.disabled) return;
    pick(opt);
    handleClose();
    focusTrigger();
  };

  // Clear selection
  const handleClear = () => {
    onChange(isLookup ? '' : null);
    handleClose();
    focusTrigger();
  };

  const choose = (i) => {
    const row = rows[i];
    if (!row) return;
    if (row.clear) handleClear();
    else handleSelect(row);
  };

  // Next enabled row from `from` in direction `dir`, stopping at the ends.
  const step = (from, dir, count = 1) => {
    let i = from;
    let found = from;
    for (let moved = 0; moved < count;) {
      i += dir;
      if (i < 0 || i >= rows.length) break;
      if (enabled(i)) { found = i; moved++; }
    }
    return found;
  };
  const first = () => step(-1, 1);
  const last = () => step(rows.length, -1);

  // Type-to-jump: the first option starting with the typed text. A repeated single letter
  // cycles through the options starting with it, as a native select does.
  const typeaheadMatch = (key, list, current) => {
    const now = Date.now();
    const t = typeahead.current;
    t.text = now - t.at > TYPEAHEAD_RESET_MS ? key : t.text + key;
    t.at = now;
    const text = t.text.toLowerCase();
    const cycling = text.length > 1 && [...text].every(c => c === text[0]);
    const prefix = cycling ? text[0] : text;
    const start = cycling || text.length === 1 ? current + 1 : current;
    for (let n = 0; n < list.length; n++) {
      const i = (Math.max(start, 0) + n) % list.length;
      const opt = list[i];
      if (opt && !opt.disabled && !opt.clear && String(opt.display ?? '').toLowerCase().startsWith(prefix)) return i;
    }
    return -1;
  };

  // Closed: change the value in place, as a native select does.
  const moveValue = (target) => {
    const opt = normalizedOptions[target];
    if (opt && !opt.disabled && !isSelected(opt)) pick(opt);
  };
  const currentIndex = () => normalizedOptions.findIndex(isSelected);

  const isPrintable = (e) => e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

  const handleKeyDown = (e) => {
    if (disabled) return;
    // Any other key ends a type-to-jump run.
    if (!isPrintable(e)) typeahead.current.text = '';

    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'F4' || (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp'))) {
        e.preventDefault();
        handleOpen();
        return;
      }
      const cur = currentIndex();
      const list = normalizedOptions;
      const nextEnabled = (from, dir) => {
        for (let i = from + dir; i >= 0 && i < list.length; i += dir) if (!list[i].disabled) return i;
        return -1;
      };
      let target = -1;
      if (e.key === 'ArrowDown') target = nextEnabled(cur, 1);
      else if (e.key === 'ArrowUp') target = cur < 0 ? nextEnabled(-1, 1) : nextEnabled(cur, -1);
      else if (e.key === 'Home') target = nextEnabled(-1, 1);
      else if (e.key === 'End') target = nextEnabled(list.length, -1);
      else if (isPrintable(e)) target = typeaheadMatch(e.key, list, cur);
      else return;
      e.preventDefault();
      if (target >= 0) moveValue(target);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        if (e.altKey) { handleClose(); focusTrigger(); return; }
        setActiveIndex(i => (i < 0 ? first() : step(i, e.key === 'ArrowDown' ? 1 : -1)));
        return;
      case 'PageDown':
      case 'PageUp':
        e.preventDefault();
        setActiveIndex(i => step(i < 0 ? -1 : i, e.key === 'PageDown' ? 1 : -1, PAGE_STEP));
        return;
      case 'Home':
      case 'End':
        // In the search box Home / End move the caret once there is text to move through.
        if (showSearch && search) return;
        e.preventDefault();
        setActiveIndex(e.key === 'Home' ? first() : last());
        return;
      case 'Enter':
        e.preventDefault();
        if (enabled(activeIndex)) choose(activeIndex);
        return;
      case 'Escape':
        // Stop here so an enclosing modal's Escape doesn't close it too.
        e.preventDefault();
        e.stopPropagation();
        handleClose();
        focusTrigger();
        return;
      case 'Tab':
        // Focus returns to the trigger before the browser moves it, so Tab continues from the
        // control rather than from the portalled list at the end of the page.
        handleClose();
        focusTrigger();
        return;
      default:
        if (!showSearch && isPrintable(e)) {
          e.preventDefault();
          const i = typeaheadMatch(e.key, rows, activeIndex);
          if (i >= 0) setActiveIndex(i);
        }
    }
  };

  // Focus the search box when opened; without one, focus stays on the trigger.
  useEffect(() => {
    if (!isOpen) return;
    if (showSearch) setTimeout(() => searchInputRef.current?.focus(), 0);
    else focusTrigger();
  }, [isOpen, showSearch]);

  // A new search highlights the first match (not the clear row above it).
  useEffect(() => {
    if (isOpen && search) setActiveIndex(step(showClear ? 0 : -1, 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Keep the highlighted row in view: centred when the list opens, nearest edge as it moves.
  const justOpened = useRef(false);
  useLayoutEffect(() => {
    if (isOpen) justOpened.current = true;
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    const el = document.getElementById(optionDomId(activeIndex));
    if (el && optionsRef.current) el.scrollIntoView?.({ block: justOpened.current ? 'center' : 'nearest' });
    justOpened.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeIndex]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClose]);

  // Close on Escape when focus has wandered off the control (the key handler covers the rest)
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') handleClose();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Recalculate position on scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    const handleReposition = (e) => {
      // Scrolling the list itself doesn't move the trigger.
      if (e?.target instanceof Node && dropdownRef.current?.contains(e.target)) return;
      calculatePosition();
    };
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen, calculatePosition]);

  const currentDisplay = displayValue || (hasValue ? String(value) : '');
  const activeId = isOpen && activeIndex >= 0 && rows[activeIndex] ? optionDomId(activeIndex) : undefined;

  return (
    <>
      {/* Trigger */}
      <div
        ref={containerRef}
        id={id}
        title={title}
        style={style}
        className={`stui-searchselect-trigger ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''} ${sizeClass} ${className}`}
        onClick={(e) => { onClick?.(e); if (isOpen) handleClose(); else handleOpen(); }}
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={dropdownId}
        aria-disabled={disabled || undefined}
        aria-activedescendant={showSearch ? undefined : activeId}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
      >
        <span className={`stui-searchselect-value ${!hasValue ? 'placeholder' : ''}`}>
          <span className="stui-searchselect-value-text">{hasValue ? currentDisplay : placeholder}</span>
          {sizer && <span className="stui-searchselect-sizer" aria-hidden="true">{sizer}</span>}
        </span>
        <ChevronDown size={14} className="stui-searchselect-chevron" />
      </div>

      {/* Dropdown — portalled to document.body so it escapes any parent stacking
          context (frozen datasheet columns, related-section transforms, etc.) that
          would otherwise trap position:fixed and hide it behind adjacent rows. */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          id={dropdownId}
          role="listbox"
          className={`stui-searchselect-dropdown ${openAbove ? 'open-above' : ''} ${menuSizeClass}`}
          // React bubbles events out of a portal to the trigger's ancestors; a native select's popup
          // reaches nothing on the page, so a row or card click handler must not see these either.
          onMouseDown={(e) => { e.stopPropagation(); if (e.target !== searchInputRef.current) e.preventDefault(); }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {/* Search input */}
          {showSearch && (
            <div className="stui-searchselect-search">
              <Search size={14} className="stui-searchselect-search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search..."
                className="stui-searchselect-search-input"
                aria-controls={dropdownId}
                aria-activedescendant={activeId}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {/* Options list */}
          <div className="stui-searchselect-options" ref={optionsRef}>
            {rows.length === 0 || (showClear && filteredOptions.length === 0) ? (
              <>
                {showClear && renderClear(0)}
                <div className="stui-searchselect-empty">No matches found</div>
              </>
            ) : (
              rows.map((row, i) => {
                if (row.clear) return renderClear(i);
                const selected = isSelected(row);
                return (
                  <div
                    key={`${row.id}`}
                    id={optionDomId(i)}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={row.disabled || undefined}
                    className={`stui-searchselect-option ${selected ? 'selected' : ''} ${i === activeIndex ? 'active' : ''} ${row.disabled ? 'disabled' : ''}`}
                    onMouseEnter={() => !row.disabled && setActiveIndex(i)}
                    onClick={() => handleSelect(row)}
                  >
                    <span className="stui-searchselect-option-check">
                      {selected && <Check size={12} />}
                    </span>
                    <span className="stui-searchselect-option-text">{row.display}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );

  function renderClear(i) {
    return (
      <div
        key="__clear"
        id={optionDomId(i)}
        role="option"
        aria-selected={false}
        className={`stui-searchselect-option stui-searchselect-clear ${i === activeIndex ? 'active' : ''}`}
        onMouseEnter={() => setActiveIndex(i)}
        onClick={handleClear}
      >
        <span className="stui-searchselect-option-check" />
        <span className="stui-searchselect-option-text">(Clear selection)</span>
      </div>
    );
  }
});

SearchSelect.displayName = 'SearchSelect';

export default SearchSelect;
